import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { supabase } from "../Services/supabaseClient";
import { useCompany } from "../context/CompanyContext";
import "./ResourcesBreakdown.css";
import {
  PlusCircle,
  Trash2,
  Package,
  Percent,
  Timer,
  Hash,
  Calculator,
} from "lucide-react";

const ResourcesBreakdown = ({
  resources,
  setResources,
  setCurrentTab,
  estimateData,
  setEstimateData,
}) => {
  const { companyId } = useCompany();
  const [inventoryItems, setInventoryItems] = useState([]);
  const navigate = useNavigate();

  // Fetch inventory items (UNCHANGED logic)
  useEffect(() => {
    if (!companyId) return;
    (async () => {
      const { data, error } = await supabase
        .from("inventory")
        .select("id, description, amount, quantity")
        .eq("company_id", companyId);
      if (error) console.error("Error fetching inventory:", error);
      else setInventoryItems(data || []);
    })();
  }, [companyId]);

  // Add blank row (UNCHANGED)
  const addResource = () =>
    setResources([
      ...resources,
      {
        description: "",
        amount: 0,
        quantity: 1,
        utilRate: 100,
        duration: 1,
        total: "0.00",
      },
    ]);

  // Handle field changes (UNCHANGED logic)
  const handleResourceChange = (idx, field, value) => {
    const updated = [...resources];
    const row = { ...updated[idx] };
    switch (field) {
      case "description": {
        const inv = inventoryItems.find((i) => i.id === value);
        if (inv) {
          const unit = inv.quantity ? inv.amount / inv.quantity : inv.amount;
          row.description = value;
          row.amount = unit;
        } else {
          row.description = value;
        }
        break;
      }
      case "quantity":
        row.quantity = Number(value) || 0;
        break;
      case "utilRate":
        row.utilRate = Number(value) || 0;
        break;
      case "duration":
        row.duration = Number(value) || 0;
        break;
      default:
        return;
    }
    const amt = Number(row.amount) || 0;
    const qty = Number(row.quantity) || 0;
    const rate = (Number(row.utilRate) || 0) / 100;
    const dur = Number(row.duration) || 0;
    row.total = (amt * qty * rate * dur).toFixed(2);
    updated[idx] = row;
    setResources(updated);
  };

  // Save resources and move next (UNCHANGED)
  const handleNext = async () => {
    if (!companyId) {
      alert("No company selected.");
      return;
    }
    if (resources.length === 0) {
      alert("Add at least one resource.");
      return;
    }
    const {
      data: { user },
      error: userErr,
    } = await supabase.auth.getUser();
    if (userErr || !user) {
      console.error("Auth error:", userErr);
      alert("Login required.");
      return;
    }

    const toInsert = resources.map((r) => ({
      description: r.description,
      amount: Number(r.amount),
      quantity: Number(r.quantity),
      utilRate: Number(r.utilRate),
      duration: Number(r.duration),
      total: Number(r.total),
      company_id: companyId,
      user_id: user.id,
    }));

    const { error } = await supabase.from("resources").insert(toInsert);
    if (error) {
      console.error("Error inserting resources:", error);
      alert("Could not save resources.");
      return;
    }

    setEstimateData({ ...estimateData, resources });
    setCurrentTab(3);
  };

  // Delete row (UNCHANGED)
  const handleDelete = (idx) =>
    setResources(resources.filter((_, i) => i !== idx));

  // Grand total (UI-only)
  const grandTotal = useMemo(
    () =>
      resources.reduce((sum, r) => sum + (Number(r.total) || 0), 0).toFixed(2),
    [resources]
  );

  return (
    <div className="resource-breakdown-container">
      <section id="resourceBreakdown" className="panel">
        <header className="rb-head">
          <div>
            <h2>Resources Breakdown</h2>
            <p className="muted">
              Add labor/materials and set utilization & duration.
            </p>
          </div>
          <div className="rb-actions">
            <button
              onClick={addResource}
              id="addRes-btn"
              className="btn-primary"
            >
              <PlusCircle size={16} /> Add Resource
            </button>
          </div>
        </header>

        <div className="table-wrap">
          <table className="rb-table">
            <thead>
              <tr>
                <th className="num">
                  <Hash size={12} />
                </th>
                <th>Description</th>
                <th title="Unit Cost (auto from inventory)">Unit</th>
                <th className="num">Qty</th>
                <th className="num">Util %</th>
                <th className="num">Duration</th>
                <th className="num">Total</th>
                <th className="center">Action</th>
              </tr>
            </thead>
            <tbody>
              {resources.map((res, i) => {
                const unitDisplay =
                  res.amount != null
                    ? Number(res.amount).toLocaleString()
                    : "0";
                return (
                  <tr key={i}>
                    <td className="num sub">{i + 1}</td>

                    <td>
                      <div className="cell-stack">
                        <div className="with-icon">
                          <Package size={14} className="ico muted" />
                          <select
                            className="rb-input"
                            value={res.description}
                            onChange={(e) =>
                              handleResourceChange(
                                i,
                                "description",
                                e.target.value
                              )
                            }
                          >
                            <option value="">Select item…</option>
                            {inventoryItems.map((inv) => (
                              <option key={inv.id} value={inv.id}>
                                {inv.description}
                              </option>
                            ))}
                          </select>
                        </div>
                        <small className="hint">
                          Unit auto-fills from inventory price / quantity.
                        </small>
                      </div>
                    </td>

                    <td className="num faint">₦{unitDisplay}</td>

                    <td className="num">
                      <input
                        className="rb-input"
                        type="number"
                        min="0"
                        value={res.quantity}
                        onChange={(e) =>
                          handleResourceChange(i, "quantity", e.target.value)
                        }
                      />
                    </td>

                    <td className="num">
                      <div className="with-icon right">
                        <input
                          className="rb-input"
                          type="number"
                          min="0"
                          max="100"
                          value={res.utilRate}
                          onChange={(e) =>
                            handleResourceChange(i, "utilRate", e.target.value)
                          }
                        />
                        <Percent size={12} className="ico muted" />
                      </div>
                    </td>

                    <td className="num">
                      <div className="with-icon right">
                        <input
                          className="rb-input"
                          type="number"
                          min="0"
                          value={res.duration}
                          onChange={(e) =>
                            handleResourceChange(i, "duration", e.target.value)
                          }
                        />
                        <Timer size={12} className="ico muted" />
                      </div>
                    </td>

                    <td className="num strong">
                      ₦{Number(res.total || 0).toLocaleString()}
                    </td>

                    <td className="center">
                      <button
                        className="icon-btn danger"
                        title="Delete row"
                        onClick={() => handleDelete(i)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>

            <tfoot>
              <tr>
                <td colSpan="6" className="tfoot-label">
                  <span className="sum-chip">
                    <Calculator size={14} /> Grand Total
                  </span>
                </td>
                <td className="num tfoot-total">
                  ₦{Number(grandTotal).toLocaleString()}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>

          {!resources.length && (
            <div className="empty-state">
              <p>No resources yet.</p>
              <button onClick={addResource} className="btn-ghost">
                <PlusCircle size={16} /> Add your first resource
              </button>
            </div>
          )}
        </div>

        <footer className="rb-footer">
          <div className="hint">
            Tip: Utilization × Duration × Qty × Unit = Row Total
          </div>
          <div className="rb-nav">
            <button
              onClick={() => navigate("/dashboard")}
              className="btn-ghost"
            >
              Cancel
            </button>
            <button onClick={() => setCurrentTab(1)} className="btn-ghost">
              Previous
            </button>
            <button onClick={handleNext} className="btn-primary">
              Next
            </button>
          </div>
        </footer>
      </section>
    </div>
  );
};

ResourcesBreakdown.propTypes = {
  resources: PropTypes.array.isRequired,
  setResources: PropTypes.func.isRequired,
  setCurrentTab: PropTypes.func.isRequired,
  estimateData: PropTypes.object,
  setEstimateData: PropTypes.func.isRequired,
};

export default ResourcesBreakdown;
